import React, { forwardRef, useState, useRef, useCallback, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Form } from '@ant-design/compatible';
import { Row, Col, Select } from 'antd';

import { companyType as companyTypeConstant, companyTypeArr } from '~constants/landlord';
import Input from 'common/components/input';
import Modal from 'common/components/modal';
import MyMap from '~components/my-map';
import useStyles from '../../style';

const CITY_BELONG_CHINA = ['HK', 'TW', 'MO'];
const FOUR_MUNICIPALITY = ['北京市', '上海市', '天津市', '重庆市'];

const BusinessInfoModal = forwardRef(
  ({ form, landlord = {}, updateLandlordLoading = false, getLandlord, updateLandlord }, ref) => {
    const { getFieldDecorator, setFieldsValue, resetFields, validateFields } = form;
    const { t } = useTranslation();
    const classes = useStyles();

    const addressInputRef = useRef(null);
    const cityInputRef = useRef(null);

    const [visible, setVisible] = useState(false);
    const handleOpenModal = () => {
      setVisible(true);

      document.addEventListener('googleApiMounted', handleGoogleMapSearch);
    };

    const handleCancelModal = () => {
      setVisible(false);

      resetFields([
        'name',
        'companyType',
        'businessScale',
        'businessNumber',
        'billingPostalCode',
        'billingAddress',
        'billingCityName',
      ]);

      document.removeEventListener('googleApiMounted', handleGoogleMapSearch);
    };

    const handleConfirmClick = () => {
      validateFields((err, values) => {
        if (!err) {
          if (landlord.businessType === 'INDIVIDUAL') {
            const {
              businessScale,
              businessNumber,
              billingPostalCode,
              billingAddress,
              governmentIssuedId,
              firstName,
              lastName,
              billingCityName,
            } = values;
            const cityResult = billingCityName?.split(',');
            updateLandlord(
              {
                id: landlord.id,
                billingCity: cityResult?.[0],
                billingCountry: cityResult?.[1]?.replace(/^\s/, ''),
                governmentIssuedId,
                businessScale: Number(businessScale),
                businessNumber,
                billingPostalCode,
                billingAddress,
                firstName,
                lastName,
              },
              () => {
                handleCancelModal();

                getLandlord();
              },
            );
          } else {
            const {
              name,
              companyType,
              businessScale,
              businessNumber,
              billingPostalCode,
              billingAddress,
              billingCityName,
            } = values;

            const cityResult = billingCityName?.split(',');

            updateLandlord(
              {
                id: landlord.id,
                name,
                companyType,
                businessScale: Number(businessScale),
                businessNumber,
                billingPostalCode,
                billingAddress,
                billingCity: cityResult?.[0],
                billingCountry: cityResult?.[1]?.replace(/^\s/, ''),
              },
              () => {
                handleCancelModal();

                getLandlord();
              },
            );
          }
        }
      });
    };
    const handleGoogleMapSearch = useCallback(() => {
      if (addressInputRef?.current?.input) {
        const request = {
          types: ['address'],
        };
        const searchAddressService = new window.google.maps.places.Autocomplete(
          addressInputRef.current?.input,
          request,
        );
        searchAddressService.addListener('place_changed', () => {
          const place = searchAddressService.getPlace();

          const postalCodeComponent = place.address_components.find(item =>
            item.types?.includes('postal_code'),
          );

          setFieldsValue({
            billingAddress: place.formatted_address,
            billingPostalCode: postalCodeComponent?.long_name ?? '',
          });
        });
      }

      if (cityInputRef?.current?.input) {
        const request = {
          types: ['(cities)'],
        };
        const searchCityService = new window.google.maps.places.Autocomplete(
          cityInputRef.current?.input,
          request,
        );
        searchCityService.addListener('place_changed', () => {
          const { address_components } = searchCityService.getPlace();

          let country = null;
          let city = null;

          address_components.forEach(address => {
            if (
              address.types.includes('country') &&
              CITY_BELONG_CHINA.includes(address.short_name)
            ) {
              country = 'China';
              city = address.long_name;
            } else {
              if (address.types.includes('country')) {
                country = address.long_name;
              }

              if (
                ['postal_town', 'locality'].some(item => address.types.includes(item)) ||
                FOUR_MUNICIPALITY.includes(address.long_name)
              ) {
                city = address.long_name;
              }
            }
          });

          setFieldsValue({
            billingCityName: `${city}, ${country}`,
          });
        });
      }
    }, [setFieldsValue]);

    useImperativeHandle(ref, () => ({
      handleOpenModal,
    }));
    const isIndividualLandlords = landlord?.businessType === 'INDIVIDUAL';

    return (
      <Modal
        title={t('cms.landlord.modal.title.edit_business_info')}
        width={880}
        visible={visible}
        wrapClassName={classes.modalWrapper}
        okText={t('cms.landlord.modal.confirm.button')}
        onCancel={handleCancelModal}
        onOk={handleConfirmClick}
        cancelButtonProps={{
          className: 'ant-btn-cancel',
        }}
        okButtonProps={{
          loading: updateLandlordLoading,
          className: 'ant-btn-confirm',
        }}
      >
        <Form colon={false} className={classes.formWrapper}>
          <If condition={isIndividualLandlords}>
            <Row gutter={50}>
              <Col span={12}>
                <Form.Item label={`${t('cms.landlord_detail.edit_modal.firstName')}:`}>
                  {getFieldDecorator('firstName', {
                    initialValue: landlord?.firstName || null,
                    rules: [
                      {
                        required: true,

                        message: t('cms.landlord_detail.edit_modal.firstName.error'),
                        whitespace: true,
                      },
                      {
                        pattern: new RegExp(/^[a-zA-Z@~`!@#$%^&*()_=+\\\\';:\"\\/?>.<,-]+$/i),
                        message: t(
                          'cms.landlord_detail.edit_modal.firstName.only_enlish_character',
                        ),
                      },
                    ],
                  })(<Input placeholder={t('cms.landlord_detail.edit_modal.firstName')} />)}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={`${t('cms.landlord_detail.edit_modal.lastName')}:`}>
                  {getFieldDecorator('lastName', {
                    initialValue: landlord?.lastName || null,
                    rules: [
                      {
                        required: true,

                        message: t('cms.landlord_detail.edit_modal.lastName.error'),
                        whitespace: true,
                      },
                      {
                        pattern: new RegExp(/^[a-zA-Z@~`!@#$%^&*()_=+\\\\';:\"\\/?>.<,-]+$/i),
                        message: t(
                          'cms.landlord_detail.edit_modal.firstName.only_enlish_character',
                        ),
                      },
                    ],
                  })(<Input placeholder={t('cms.landlord_detail.edit_modal.lastName')} />)}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={`${t('business_opportunity')}:`}>
                  {getFieldDecorator('businessScale', {
                    initialValue: landlord.businessScale,
                    rules: [
                      {
                        required: true,
                        message: t('cms.landlord.modal.error.blank'),
                      },
                    ],
                    validateTrigger: 'onBlur',
                  })(<Input />)}
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label={`${t(
                    'cms.landlord_detail.individual.bussiness_info.government-issued_id',
                  )}:`}
                >
                  {getFieldDecorator('governmentIssuedId', {
                    initialValue: landlord.governmentIssuedId,
                    rules: [
                      {
                        required: true,
                        message: t(
                          'cms.landlord_detail.individual.bussiness_info.government-issued_id.error',
                        ),
                      },
                      {
                        max: 250,
                        message: t(
                          'cms.landlord_detail.individual.bussiness_info.government-issued_id.max_character_number',
                        ),
                      },
                      {
                        pattern: /^[^\s]*$/,
                        message: t(
                          'cms.landlord_detail.individual.bussiness_info.government-issued_id.no_space',
                        ),
                      },
                    ],
                  })(
                    <Input
                      placeholder={t(
                        'cms.landlord_detail.individual.bussiness_info.government-issued_id.placeholder',
                      )}
                    />,
                  )}
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label={`${t('cms.landlord_detail.individual.bussiness_info.address')}:`}>
                  {getFieldDecorator('billingAddress', {
                    initialValue: landlord.billingAddress,
                    rules: [
                      {
                        required: true,
                        message: t('cms.landlord.modal.error.blank'),
                      },
                    ],
                    validateTrigger: 'onBlur',
                  })(<Input ref={addressInputRef} />)}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={`${t('city')}`}>
                  {getFieldDecorator('billingCityName', {
                    initialValue: `${landlord.billingCity?.name}, ${landlord.billingCountry?.name}`,
                    rules: [
                      {
                        required: true,
                        message: t('cms.landlord.modal.error.blank'),
                      },
                    ],
                    validateTrigger: 'onBlur',
                  })(
                    <Input
                      ref={cityInputRef}
                      placeholder={t('cms.landlord.modal.billing_city.placeholder')}
                    />,
                  )}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={`${t('zip_code')}:`}>
                  {getFieldDecorator('billingPostalCode', {
                    initialValue: landlord.billingPostalCode,
                    rules: [
                      {
                        max: 10,
                        message: t('cms.landlord.modal.error.billing_postal_code'),
                      },
                      {
                        required: true,
                        message: t('cms.landlord.modal.error.blank'),
                      },
                    ],
                    validateTrigger: 'onBlur',
                  })(<Input />)}
                </Form.Item>
              </Col>
              <Col span={24} style={{ display: 'none' }}>
                <MyMap
                  t={t}
                  lastSearched={{
                    lat: 0,
                    lng: 0,
                  }}
                  form={form}
                  value={{
                    action: 'default',
                    value: { lat: 0, lng: 0 },
                  }}
                />
              </Col>
            </Row>
          </If>
          <If condition={!isIndividualLandlords}>
            <Row gutter={50}>
              <Col span={12}>
                <Form.Item label={t('company_name')}>
                  {getFieldDecorator('name', {
                    initialValue: landlord.name,
                    rules: [
                      {
                        required: true,
                        message: t('cms.landlord.modal.error.blank'),
                      },
                      {
                        max: 150,
                        message: t('cms.landlord.modal.error.landlord_name.limit'),
                      },
                    ],
                    normalize: value => value.replace(/[\u4e00-\u9fa5]/g, ''),
                    validateTrigger: 'onBlur',
                  })(<Input placeholder={t('cms.landlord.modal.landlord_name.placeholder')} />)}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={t('cms.landlord.detail.company_type.label')} colon={false}>
                  {getFieldDecorator('companyType', {
                    initialValue: landlord.companyType ?? undefined,
                    rules: [
                      {
                        required: true,
                        message: t('cms.landlord.detail.company_type.error.required'),
                      },
                    ],
                  })(
                    <Select placeholder={t('cms.landlord.detail.company_type.placeholder')}>
                      <For of={companyTypeArr} each="companyType" index="index">
                        <Select.Option key={index} value={companyType}>
                          {t(
                            `cms.landlord.detail.company_type.${companyTypeConstant[
                              companyType
                            ].toLowerCase()}`,
                          )}
                        </Select.Option>
                      </For>
                    </Select>,
                  )}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={t('business_opportunity')}>
                  {getFieldDecorator('businessScale', {
                    initialValue: landlord.businessScale,
                    rules: [
                      {
                        required: true,
                        message: t('cms.landlord.modal.error.blank'),
                      },
                    ],
                    validateTrigger: 'onBlur',
                  })(<Input />)}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={t('registered_business_number')}>
                  {getFieldDecorator('businessNumber', {
                    initialValue: landlord.businessNumber,
                    rules: [
                      {
                        required: true,
                        message: t('cms.landlord.modal.error.business_number'),
                        whitespace: true,
                      },
                    ],
                    validateTrigger: 'onBlur',
                  })(<Input placeholder={t('cms.landlord.modal.business_number.placeholder')} />)}
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label={t('registered_business_address')}>
                  {getFieldDecorator('billingAddress', {
                    initialValue: landlord.billingAddress,
                    rules: [
                      {
                        required: true,
                        message: t('cms.landlord.modal.error.blank'),
                      },
                    ],
                    validateTrigger: 'onBlur',
                  })(<Input ref={addressInputRef} />)}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={t('city')}>
                  {getFieldDecorator('billingCityName', {
                    initialValue: `${landlord.billingCity?.name}, ${landlord.billingCountry?.name}`,
                    rules: [
                      {
                        required: true,
                        message: t('cms.landlord.modal.error.blank'),
                      },
                    ],
                    validateTrigger: 'onBlur',
                  })(
                    <Input
                      ref={cityInputRef}
                      placeholder={t('cms.landlord.modal.billing_city.placeholder')}
                    />,
                  )}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={t('zip_code')}>
                  {getFieldDecorator('billingPostalCode', {
                    initialValue: landlord.billingPostalCode,
                    rules: [
                      {
                        max: 10,
                        message: t('cms.landlord.modal.error.billing_postal_code'),
                      },
                      {
                        required: true,
                        message: t('cms.landlord.modal.error.blank'),
                      },
                    ],
                    validateTrigger: 'onBlur',
                  })(<Input />)}
                </Form.Item>
              </Col>
              <Col span={24} style={{ display: 'none' }}>
                <MyMap
                  t={t}
                  lastSearched={{
                    lat: 0,
                    lng: 0,
                  }}
                  form={form}
                  value={{
                    action: 'default',
                    value: { lat: 0, lng: 0 },
                  }}
                />
              </Col>
            </Row>
          </If>
        </Form>
      </Modal>
    );
  },
);

BusinessInfoModal.displayName = 'BusinessInfoModal';

BusinessInfoModal.propTypes = {
  form: PropTypes.object.isRequired,
  landlord: PropTypes.object.isRequired,
  updateLandlordLoading: PropTypes.bool.isRequired,
  getLandlord: PropTypes.func.isRequired,
  updateLandlord: PropTypes.func.isRequired,
};

export default Form.create({
  name: 'Business Info',
})(BusinessInfoModal);
